// markdown-renderer.ts — interactive preview orchestrator.
//
// Coordinates the headless render-core/ passes (preprocess, collapsible,
// post-process note-links, attachments, math, mermaid) into the #preview
// surface, plus the PreviewRenderer theme/cache hooks and the beforeprint
// handler. The string→HTML passes themselves live under render-core/; this
// file owns only the live-surface wiring. (The edit/view toggle is gone.)

import {
  LABYRINTH_NOTE,
  getCurrentFileName,
  getLinkedNoteChain,
  getLoadNoteGeneration,
  getReadOnlyViewActive,
  previewDiv,
  reflectActiveView,
  saveChain,
  setLastSavedContent,
  setLinkedNoteChain,
} from '../core/app-state.js';
import { setupPreviewTaskCheckboxes } from '../notes/file-list.js';
import { renderLabyrinth } from '../notes/labyrinth-view.js';
import { safeRenderMarkdown } from '../utils/sanitize.js';
import {
  buildMermaidVarMap,
  markOverflowingMathContainers,
  setupClickableMathFormulas,
} from './math-eval.js';
import { NoteEditor } from './note-editor.js';
import { alignTableColumns, wrapTablesForScroll } from './table-features.js';
import { renderCache, resetRenderCache } from './preview-render-cache.js';
// Headless render engine — the markdown→HTML passes live under render-core/
// (formerly editor/preview/*). They are shared by this interactive orchestrator,
// HTML export, the task panel, and the read-only generated views. Imported here
// only for renderPreview's own use; other consumers import render-core/* directly
// (this file no longer re-exports the passes — see the export block at the end).
import './render-core/marked-config.js'; // side-effect: configure global marked
import { resolveAttachments } from './render-core/attachments.js';
import { setupCollapsibleHeadings } from './render-core/collapsible.js';
import {
  ensureKatexLoaded,
  hasMathSyntax,
  invalidateKatexCache,
  wrapMathFragments,
} from './render-core/math.js';
import { invalidateMermaidCache, renderMermaidDiagrams } from './render-core/mermaid.js';
import { setupNoteLinks, styleTaskListItems } from './render-core/post-process.js';
import { preprocessMarkdown } from './render-core/preprocess.js';

// The KaTeX lazy loader (ensureKatexLoaded) now
// lives with the math pass in render-core/math.js, and the global `marked`
// configuration (indented-code suppression + the attachment-image renderer) in
// render-core/marked-config.js — both imported above. They moved out of this
// host so the headless export path can reach them without importing the
// interactive orchestrator, and so deleting the on-screen surface can't drop
// the load-bearing marked renderer.

// Non-task checkbox source pattern ([ ] / [x] without a leading "- ").
const _PLAIN_CB_RE = /^(\s*)\[( |[xX])\]\s/;

// Wire toggle handlers for plain (non-task) `[ ]` checkboxes in the rendered
// preview. Lives here in the INTERACTIVE orchestrator (not render-core/
// post-process) because it reaches into NoteEditor + live note state — keeping
// the headless render-core engine free of any editor/notes import (the export,
// graph, and print paths import post-process but never this).
function setupPlainCheckboxes(container: any): void {
  const checkboxes = container.querySelectorAll('input[data-plain-cb]');
  checkboxes.forEach((cb: any) => {
    cb.disabled = false;
    cb.addEventListener('change', () => {
      // Find all plain checkboxes in source and toggle the matching one
      const lines = NoteEditor.getContent().split('\n');
      let cbIndex = 0;
      const allPlainCbs = container.querySelectorAll('input[data-plain-cb]');
      const targetIdx = Array.from(allPlainCbs).indexOf(cb);

      let inFence = false;
      for (let i = 0; i < lines.length; i++) {
        if (/^[ \t]*(`{3,}|~{3,})/.test(lines[i])) {
          inFence = !inFence;
          continue;
        }
        if (inFence) continue;
        const m = lines[i].match(_PLAIN_CB_RE);
        if (m && !/^\s*- \[/.test(lines[i])) {
          if (cbIndex === targetIdx) {
            lines[i] = lines[i].replace(_PLAIN_CB_RE, `$1[${cb.checked ? 'x' : ' '}] `);
            break;
          }
          cbIndex++;
        }
      }
      NoteEditor.replaceAll(lines.join('\n'));
      if (getCurrentFileName()) {
        const updated = NoteEditor.getContent();
        // Update getLastSavedContent() immediately so a pending auto-save timer
        // does not overwrite this checkbox toggle with stale content.
        setLastSavedContent(updated);
        window.NoteStorage.setNote(getCurrentFileName(), updated);
      }
      reflectActiveView();
    });
  });
}

// Cache: skip re-parsing when content hasn't changed since last render. The
// state lives in the neutral preview-render-cache leaf (Tier 5 cycle break).

function invalidatePreviewCache(): void {
  resetRenderCache();
  // Mermaid SVGs and KaTeX render output bake the active theme colours
  // into inline styles at render time, so theme switches (which invalidate
  // the preview cache) must also drop both fragment caches — otherwise
  // we'd serve stale-coloured diagrams or formulas.
  invalidateMermaidCache();
  invalidateKatexCache();
}

// Re-render the preview after a theme change if we're currently showing it.
// Safe to call from any module — no-ops when the user is in edit mode.
/** Re-renders the preview after a theme change if shown. */
function rerenderPreviewForThemeChange(): void {
  invalidatePreviewCache();
  if (getReadOnlyViewActive()) renderPreview();
}

async function renderPreview(): Promise<void> {
  // Reset any overflow override left by the Labyrinth view so normal notes scroll correctly.
  previewDiv.style.overflow = '';

  // Labyrinth: delegate to the labyrinth-view canvas renderer (same cache-bust
  // discipline — renderLabyrinth() clears previewDiv.innerHTML).
  if (getCurrentFileName() === LABYRINTH_NOTE) {
    renderCache.html = null;
    renderCache.file = null;
    await renderLabyrinth();
    return;
  }

  const _currentContent = NoteEditor.getContent();

  // Cache hit: the previewDiv DOM is already fully built (it was hidden, not
  // destroyed, when the user switched to edit mode). Event listeners, KaTeX
  // typesetting, and resolved attachments are all still intact — return early
  // so toggling back to view is instantaneous for unchanged content.
  if (
    renderCache.file === getCurrentFileName() &&
    renderCache.content === _currentContent &&
    renderCache.html !== null
  ) {
    return;
  }

  // Capture the note name and generation at the start of rendering so we can
  // detect if the user navigated away during any of the async steps below.
  const _renderTarget = getCurrentFileName();
  const _renderGen = getLoadNoteGeneration();
  // True once the user has navigated to a different note (or triggered a fresh
  // load of the same one) since this render began — checked after each async
  // step so a stale render stops touching the DOM.
  const _navigatedAway = () =>
    _renderGen !== getLoadNoteGeneration() || getCurrentFileName() !== _renderTarget;

  renderCache.html = safeRenderMarkdown(preprocessMarkdown(_currentContent));
  renderCache.file = getCurrentFileName();
  renderCache.content = _currentContent;
  previewDiv.innerHTML = renderCache.html;
  styleTaskListItems(previewDiv);
  // Collapsible headings must be set up BEFORE note-links so that when
  // setupNoteLinks runs, anchor elements inside <summary> already exist
  // in their final DOM positions and receive click handlers correctly.
  // (cloneNode used inside setupCollapsibleHeadings does not copy listeners.)
  setupCollapsibleHeadings(previewDiv);
  await setupNoteLinks(previewDiv);

  // If the user navigated to a different note while we were awaiting async
  // operations, stop updating the DOM to prevent "back and forth" flicker.
  if (_navigatedAway()) return;

  // H1 title: clicking navigates back to the previously accessed note (breadcrumb).
  previewDiv.querySelectorAll('h1').forEach((h1: any) => {
    if (getLinkedNoteChain().length > 0) {
      h1.classList.add('note-title-back');
      h1.title = `Back to "${getLinkedNoteChain()[0]}"`;
    }
    h1.addEventListener('click', (e: any) => {
      if ((e.target as any).closest('a')) return; // let link clicks pass through unobstructed
      if (getLinkedNoteChain().length === 0) return;
      const prevNote = getLinkedNoteChain()[0];
      setLinkedNoteChain(getLinkedNoteChain().slice(1));
      saveChain();
      window.loadNote(prevNote, true);
    });
  });

  alignTableColumns(previewDiv);
  wrapTablesForScroll(previewDiv);
  setupPreviewTaskCheckboxes();
  setupPlainCheckboxes(previewDiv);
  await resolveAttachments(previewDiv);

  // Staleness check after attachment resolution (can be slow for many images).
  if (_navigatedAway()) return;

  // Only scan the whole note for math variables when it actually contains a
  // mermaid diagram. buildMermaidVarMap walks the entire source (O(content
  // length)), and the resulting map is consumed solely by renderMermaidDiagrams
  // — which itself no-ops without a mermaid code block. Notes without diagrams
  // (the common case) skip the full-note math scan entirely.
  const _mathVarMap = previewDiv.querySelector('pre code.language-mermaid')
    ? buildMermaidVarMap(_currentContent)
    : undefined;
  await renderMermaidDiagrams(previewDiv, _mathVarMap);
  // Lazy-load KaTeX only if the source actually contains math syntax.
  // Notes without $/\(/\[ skip the ~280 KB script-load entirely.
  if (!window.katex && hasMathSyntax(_currentContent)) {
    try {
      await ensureKatexLoaded();
    } catch {
      /* fall through */
    }
    if (_navigatedAway()) return;
  }
  if (window.katex && hasMathSyntax(_currentContent)) {
    // Wrap and render math fragments synchronously. Cache hits splice
    // pre-rendered HTML; misses call katex.renderToString in the same pass
    // and populate the cache so subsequent renders of the same fragment
    // are instant.
    wrapMathFragments(previewDiv);
    setupClickableMathFormulas();
    // Defer until the browser has completed layout for the new KaTeX nodes;
    // reading getBoundingClientRect synchronously here yields stale values
    // and would falsely mark containers as overflowing.
    requestAnimationFrame(markOverflowingMathContainers);
  }

  // Settings used to inject interactive controls into the rendered Settings
  // note. That UI moved to the Settings side-panel; if any vault still has
  // a stale `Settings` note, it renders as plain markdown now.
}

// ── Sync settings UI ─────────────────────────────────────────────────────
// Implementation in editor/sync-panel.js.

// ── Encryption settings UI ───────────────────────────────────────────────
// Implementation in editor/encryption-panel.js.

// (refreshSettingsPickerUI removed: it only ever updated colour pickers inside
// the old rendered Settings note. Settings moved to the side panel and the
// Settings/Calendars note is no longer a read-only generated view — only the
// Labyrinth is — so there is nothing for it to refresh.)

// Cross-module dispatch surface — used by powersync-bootstrap (refresh after
// sync), theme-engine (re-render after applied theme), and any other module
// that needs to nudge the live preview.
window.PreviewRenderer = {
  invalidateCache: invalidatePreviewCache,
  rerenderForThemeChange: rerenderPreviewForThemeChange,
};

// Phase 4.5: window.PreviewRenderer is set above; this block re-exposes
// the bare-name functions that other domains call into. Removed in 4.7.
if (typeof window !== 'undefined') {
  window.renderPreview = renderPreview;
  // window.renderMathIn / window._hasMathSyntax are installed by render-core/math.js.
  // purgeAttachmentCacheForNote is reached by ESM import (note-manager) — no shim.
}

// Print: the @media print stylesheet (base.css) hides #cm-editor and prints
// #preview. For a regular note (always in edit mode now) #preview is otherwise
// empty, so a naive Cmd-P would print a blank page. Populate it from the current
// note before the browser paints the print view. renderPreview() sets
// previewDiv.innerHTML synchronously (text, tables, task styling, collapsibles);
// the async passes — attachments, math, mermaid — are best-effort. #preview is
// display:none on screen, so this never causes a visible flash.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('beforeprint', () => {
    // renderPreview is async: a synchronous throw becomes a REJECTED PROMISE,
    // which the old sync try/catch could never observe. Swallow via .catch so a
    // render failure never surfaces as an unhandled rejection (and never blocks
    // the print dialog).
    renderPreview().catch(() => {
      /* never block the print dialog */
    });
  });
}

// The orchestrator's public surface is just its own live-preview entry points:
// renderPreview (the #preview render coordinator) and PreviewRenderer (the
// theme/cache hooks). Every render-core pass (preprocess, collapsible,
// post-process, math, mermaid, attachments) that this file once re-exported is
// now imported by its consumers DIRECTLY from render-core/* — export-html,
// file-list, schedule, navigate, block-widgets — so those re-exports had no
// named importer left and were pruned (D011). The symbols are still imported
// above where renderPreview uses them internally; they're just no longer part
// of this module's exported API.
export const PreviewRenderer = window.PreviewRenderer;
// preprocessMarkdown is kept because the markdown-preprocess test imports it
// through this module's ESM export (the retired window.preprocessMarkdown shim's
// replacement); production consumers import it from render-core/preprocess.js.
export { preprocessMarkdown, renderPreview };
